import React from 'react';
import cx from 'classnames';
import styles from './MobileLandingView.module.css';
import { LOGO_IMAGE } from '../../constants/appConstants';
import { getMobilePlatform, MobilePlatform } from '../../util/platformUtil';
import { openGeotraxApp } from '../../util/appLinkUtil';
import {
  APP_STORE_BUTTON_TEXT,
  APP_STORE_URL,
  GET_THE_APP_TEXT,
  MOBILE_LANDING_PARAGRAPHS,
  MOBILE_LANDING_TITLE,
  OPEN_APP_TEXT,
  PLAY_STORE_BUTTON_TEXT,
  PLAY_STORE_URL,
} from '../../constants/mobileLandingConstants';

export interface MobileLandingViewProps {
  // Injectable for testing; defaults to the real platform at render time.
  platform?: MobilePlatform;
  // Injectable for testing; defaults to the real deep-link / store launcher.
  launchApp?: (platform: MobilePlatform) => void;
}

interface StoreLinkProps {
  href: string;
  text: string;
  dataTest: string;
}

function StoreLink({ href, text, dataTest }: StoreLinkProps) {
  return (
    <a
      className={cx('tt-btn', styles.storeButton)}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-test={dataTest}
    >
      {text}
    </a>
  );
}

/**
 * Public landing page for /mobile.
 */
export default function MobileLandingView({
  platform,
  launchApp = openGeotraxApp,
}: MobileLandingViewProps) {
  const resolvedPlatform = platform ?? getMobilePlatform();
  // On a phone we can try to open the app (and fall back to its store). On desktop there is no
  // installed app, so we just show both store links.
  const isMobile = resolvedPlatform === 'ios' || resolvedPlatform === 'android';

  return (
    <div className={styles.background}>
      <div className={styles.scroll}>
        <div className={styles.card} data-test="mobile-landing-card">
          <img className={styles.logo} src={LOGO_IMAGE} alt="TruckTrax Geo logo" />
          <h1 className={styles.title}>{MOBILE_LANDING_TITLE}</h1>

          {MOBILE_LANDING_PARAGRAPHS.map(paragraph => (
            <p key={paragraph} className={styles.paragraph}>
              {paragraph}
            </p>
          ))}

          {resolvedPlatform === 'ios' && (
            // Primary: App Store
            // Secondary: deep-link for those who already have it.
            <div className={styles.storeButtons}>
              <StoreLink
                href={APP_STORE_URL}
                text={APP_STORE_BUTTON_TEXT}
                dataTest="mobile-landing-app-store"
              />
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => launchApp('ios')}
                data-test="mobile-landing-open-app"
              >
                {OPEN_APP_TEXT}
              </button>
            </div>
          )}

          {resolvedPlatform === 'android' && (
            // Android's intent:// opens the app if installed and falls back to the Play Store
            // natively, so a single button covers both cases.
            <div className={styles.storeButtons}>
              <button
                type="button"
                className={cx('tt-btn', styles.openAppButton)}
                onClick={() => launchApp('android')}
                data-test="mobile-landing-open-app"
              >
                {OPEN_APP_TEXT}
              </button>
            </div>
          )}

          {!isMobile && (
            <>
              <span className={styles.getAppLabel}>{GET_THE_APP_TEXT}</span>
              <div className={cx(styles.storeButtons, styles.storeButtonsRow)}>
                <StoreLink
                  href={APP_STORE_URL}
                  text={APP_STORE_BUTTON_TEXT}
                  dataTest="mobile-landing-app-store"
                />
                <StoreLink
                  href={PLAY_STORE_URL}
                  text={PLAY_STORE_BUTTON_TEXT}
                  dataTest="mobile-landing-play-store"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
